import { z } from 'zod';
export var AccountCenterControlValue;
(function (AccountCenterControlValue) {
    AccountCenterControlValue["Off"] = "Off";
    AccountCenterControlValue["ReadOnly"] = "ReadOnly";
    AccountCenterControlValue["Edit"] = "Edit";
})(AccountCenterControlValue || (AccountCenterControlValue = {}));
/**
 * Control list of each field in the account center (profile API)
 * all fields are optional, if not set, the default value is `Off`
 * this can make the alteration of the field control easier
 */
export const accountCenterFieldControlGuard = z
    .object({
    name: z.nativeEnum(AccountCenterControlValue),
    avatar: z.nativeEnum(AccountCenterControlValue),
    profile: z.nativeEnum(AccountCenterControlValue),
    email: z.nativeEnum(AccountCenterControlValue),
    phone: z.nativeEnum(AccountCenterControlValue),
    password: z.nativeEnum(AccountCenterControlValue),
    username: z.nativeEnum(AccountCenterControlValue),
    social: z.nativeEnum(AccountCenterControlValue),
    customData: z.nativeEnum(AccountCenterControlValue),
    mfa: z.nativeEnum(AccountCenterControlValue),
    passkey: z.nativeEnum(AccountCenterControlValue),
    session: z.nativeEnum(AccountCenterControlValue),
    trustedDevice: z.nativeEnum(AccountCenterControlValue),
})
    .partial();
export const webauthnRelatedOriginsGuard = z.array(z.string());
export const accountCenterProfileFieldItemGuard = z.object({
    name: z.string(),
});
export const accountCenterProfileFieldsGuard = z.array(accountCenterProfileFieldItemGuard);
export var OrganizationCenterCreationMode;
(function (OrganizationCenterCreationMode) {
    OrganizationCenterCreationMode["Disabled"] = "disabled";
    OrganizationCenterCreationMode["All"] = "all";
    OrganizationCenterCreationMode["Roles"] = "roles";
})(OrganizationCenterCreationMode || (OrganizationCenterCreationMode = {}));
export var OrganizationManagementPermission;
(function (OrganizationManagementPermission) {
    OrganizationManagementPermission["UpdateProfile"] = "update_profile";
    OrganizationManagementPermission["ManageBranding"] = "manage_branding";
    OrganizationManagementPermission["ViewMembers"] = "view_members";
    OrganizationManagementPermission["ManageMembers"] = "manage_members";
    OrganizationManagementPermission["ManageInvitations"] = "manage_invitations";
    OrganizationManagementPermission["AssignBusinessRoles"] = "assign_business_roles";
    OrganizationManagementPermission["ManageManagementRoles"] = "manage_management_roles";
    OrganizationManagementPermission["AssignManagementRoles"] = "assign_management_roles";
    OrganizationManagementPermission["ManageSecurity"] = "manage_security";
    OrganizationManagementPermission["ManageJit"] = "manage_jit";
    OrganizationManagementPermission["ManageApplications"] = "manage_applications";
    OrganizationManagementPermission["ViewActivity"] = "view_activity";
})(OrganizationManagementPermission || (OrganizationManagementPermission = {}));
export const organizationManagementPermissions = Object.freeze(Object.values(OrganizationManagementPermission));
export const organizationCenterModulesGuard = z.object({
    profile: z.boolean(),
    branding: z.boolean(),
    members: z.boolean(),
    invitations: z.boolean(),
    managementRoles: z.boolean(),
    businessRoles: z.boolean(),
    security: z.boolean(),
    jit: z.boolean(),
    applications: z.boolean(),
    activity: z.boolean(),
    deletion: z.boolean(),
});
export const organizationCenterSettingsGuard = z.object({
    enabled: z.boolean(),
    modules: organizationCenterModulesGuard,
    creationPolicy: z.object({
        mode: z.nativeEnum(OrganizationCenterCreationMode),
        allowedRoleIds: z.array(z.string().min(1).max(21)),
        maxOrganizationsPerUser: z.number().int().min(1).max(100),
    }),
    invitationPolicy: z.object({
        allowRegistration: z.boolean(),
        expiresInDays: z.number().int().min(1).max(30),
    }),
    resourceAllowlist: z.object({
        ssoConnectorIds: z.array(z.string().min(1).max(128)),
        applicationIds: z.array(z.string().min(1).max(21)),
        organizationRoleIds: z.array(z.string().min(1).max(21)),
    }),
});
/** Safe organization-center settings exposed to the prebuilt Account Center. */
export const publicOrganizationCenterSettingsGuard = organizationCenterSettingsGuard.pick({
    enabled: true,
    modules: true,
    creationPolicy: true,
    invitationPolicy: true,
});
export const defaultOrganizationCenterSettings = Object.freeze({
    enabled: false,
    modules: {
        profile: true,
        branding: true,
        members: true,
        invitations: true,
        managementRoles: true,
        businessRoles: true,
        security: true,
        jit: true,
        applications: true,
        activity: true,
        deletion: true,
    },
    creationPolicy: {
        mode: OrganizationCenterCreationMode.Disabled,
        allowedRoleIds: [],
        maxOrganizationsPerUser: 1,
    },
    invitationPolicy: {
        allowRegistration: true,
        expiresInDays: 7,
    },
    resourceAllowlist: {
        ssoConnectorIds: [],
        applicationIds: [],
        organizationRoleIds: [],
    },
});
export const organizationManagementPermissionsGuard = z.array(z.nativeEnum(OrganizationManagementPermission));
export const deleteAccountUrlGuard = z
    .string()
    .max(2048)
    .refine((value) => value === '' ||
    ((value.startsWith('https://') || value.startsWith('http://')) &&
        z.string().url().safeParse(value).success), {
    message: 'deleteAccountUrl must be a valid http(s) URL',
});
